import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import { useChannels } from '@entities/channel';
import {
  BROADCAST_PLATFORM_LABELS,
  BROADCAST_STATUS_META,
  MEDIA_LIMITS,
  useBroadcast,
  useBroadcasts,
  useCancelBroadcast,
  useCreateBroadcast,
  useDeleteBroadcast,
  useDeleteBroadcastMedia,
  useBroadcastStats,
  useRecipientsPreview,
  useSendBroadcast,
  useUpdateBroadcast,
  useUploadBroadcastMedia,
} from '@entities/broadcast';
import type {
  Broadcast,
  BroadcastButton,
  BroadcastStats,
} from '@entities/broadcast';
import { extractErrorMessage } from '@shared/api';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface Props {
  assistantId: string;
}

const BROADCASTABLE = ['telegram', 'max'];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} МБ`
    : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
}

const isActive = (s: Broadcast['status']) => s === 'queued' || s === 'sending';
const isDraftStatus = (s: Broadcast['status']) => s === 'draft';

/**
 * Вкладка «Рассылки» (N-3-ui + N-4).
 *
 * Конструктор черновика + запуск отправки. Отправка идёт в фоне (воркер),
 * прогресс обновляется автополлингом (3с), пока рассылка активна.
 *
 * Отмена — «стоп-кран»: останавливает дальнейшую отправку, но НЕ отзывает уже
 * отправленное (платформы не дают отзыва). Это честно проговорено в UI.
 */
export function BroadcastsTab({ assistantId }: Props) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: channels, isLoading: channelsLoading } = useChannels(assistantId);
  const { data: broadcasts, isLoading, error } = useBroadcasts(assistantId);
  const removeMutation = useDeleteBroadcast(assistantId);

  const targets = useMemo(
    () => (channels ?? []).filter((c) => BROADCASTABLE.includes(c.platform)),
    [channels],
  );

  const handleDelete = (id: string) => {
    removeMutation.mutate(id, {
      onSuccess: () => message.success('Черновик удалён'),
      onError: (e) => message.error(extractErrorMessage(e)),
    });
  };

  const columns: ColumnsType<Broadcast> = [
    {
      title: 'Рассылка',
      dataIndex: 'title',
      render: (_: unknown, row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.title || 'Без заголовка'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.body_text
              ? `${row.body_text.slice(0, 60)}${row.body_text.length > 60 ? '…' : ''}`
              : 'Текст не заполнен'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Канал',
      dataIndex: 'platform',
      width: 110,
      render: (p: string) => BROADCAST_PLATFORM_LABELS[p] ?? p,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 210,
      render: (s: Broadcast['status'], row) => {
        const meta = BROADCAST_STATUS_META[s] ?? { label: s, color: 'default' };
        if (isActive(s) || s === 'done') {
          const total = row.total_count || 0;
          const done = row.sent_count + row.failed_count;
          const percent = total ? Math.round((done / total) * 100) : 0;
          return (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Tag color={meta.color}>{meta.label}</Tag>
              <Progress
                percent={s === 'done' ? 100 : percent}
                size="small"
                status={s === 'done' ? 'success' : 'active'}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {done} из {total}
                {row.failed_count > 0 ? ` · ошибок ${row.failed_count}` : ''}
              </Text>
            </Space>
          );
        }
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Создана',
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatDate(v)}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_: unknown, row) => (
        <Space>
          <Button size="small" onClick={() => setEditingId(row.id)}>
            {isDraftStatus(row.status) ? 'Открыть' : 'Смотреть'}
          </Button>
          {isDraftStatus(row.status) && (
            <Popconfirm
              title="Удалить черновик?"
              okText="Удалить"
              cancelText="Отмена"
              onConfirm={() => handleDelete(row.id)}
            >
              <Button size="small" danger type="text">
                Удалить
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (isLoading || channelsLoading) {
    return <Skeleton active paragraph={{ rows: 5 }} />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Не удалось загрузить рассылки"
        description={extractErrorMessage(error)}
      />
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Рассылки
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Сообщение всем подписчикам бота. Доступно для Telegram и MAX — виджет
            рассылку не поддерживает.
          </Text>
        </div>
        <Button
          type="primary"
          disabled={targets.length === 0}
          onClick={() => setCreateOpen(true)}
        >
          Создать рассылку
        </Button>
      </div>

      {targets.length === 0 ? (
        <Card>
          <Empty
            description={
              <Space direction="vertical" size={4}>
                <Text>Нет каналов, куда можно рассылать</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Подключите бота Telegram или MAX на вкладке «Каналы».
                </Text>
              </Space>
            }
          />
        </Card>
      ) : (
        <Table<Broadcast>
          rowKey="id"
          columns={columns}
          dataSource={broadcasts ?? []}
          pagination={false}
          locale={{ emptyText: <Empty description="Рассылок пока нет" /> }}
        />
      )}

      <CreateBroadcastModal
        assistantId={assistantId}
        open={createOpen}
        channels={targets}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          setEditingId(id);
        }}
      />

      <BroadcastEditorDrawer
        assistantId={assistantId}
        broadcastId={editingId}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Создание                                                            */
/* ------------------------------------------------------------------ */

interface CreateProps {
  assistantId: string;
  open: boolean;
  channels: { id: string; platform: string }[];
  onClose: () => void;
  onCreated: (id: string) => void;
}

function CreateBroadcastModal({
  assistantId,
  open,
  channels,
  onClose,
  onCreated,
}: CreateProps) {
  const [form] = Form.useForm<{ channel_id: string; title?: string }>();
  const createMutation = useCreateBroadcast(assistantId);

  const handleOk = async () => {
    const values = await form.validateFields();
    createMutation.mutate(
      { channel_id: values.channel_id, title: values.title ?? null, body_text: '' },
      {
        onSuccess: (b) => {
          form.resetFields();
          onCreated(b.id);
        },
        onError: (e) => message.error(extractErrorMessage(e)),
      },
    );
  };

  return (
    <Modal
      open={open}
      title="Новая рассылка"
      okText="Создать"
      cancelText="Отмена"
      confirmLoading={createMutation.isPending}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="channel_id"
          label="Канал"
          rules={[{ required: true, message: 'Выберите канал' }]}
          extra="Канал нельзя изменить после создания — получатели привязаны к нему."
        >
          <Select
            placeholder="Куда рассылаем"
            options={channels.map((c) => ({
              value: c.id,
              label: `${BROADCAST_PLATFORM_LABELS[c.platform] ?? c.platform} · ${c.id.slice(0, 8)}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="title" label="Название (для себя)">
          <Input placeholder="Например: Акция на март" maxLength={255} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Редактор + запуск                                                   */
/* ------------------------------------------------------------------ */

interface EditorProps {
  assistantId: string;
  broadcastId: string | null;
  onClose: () => void;
}

function BroadcastEditorDrawer({ assistantId, broadcastId, onClose }: EditorProps) {
  const { data: broadcast, isLoading } = useBroadcast(assistantId, broadcastId);
  const { data: recipients } = useRecipientsPreview(assistantId, broadcastId);
  const isRunningNow = broadcast ? isActive(broadcast.status) : false;
  const { data: stats, isLoading: statsLoading } = useBroadcastStats(
    assistantId,
    broadcast && broadcast.status !== 'draft' ? broadcastId : null,
    isRunningNow,
  );

  const updateMutation = useUpdateBroadcast(assistantId);
  const uploadMutation = useUploadBroadcastMedia(assistantId);
  const deleteMediaMutation = useDeleteBroadcastMedia(assistantId);
  const sendMutation = useSendBroadcast(assistantId);
  const cancelMutation = useCancelBroadcast(assistantId);

  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [buttons, setButtons] = useState<BroadcastButton[]>([]);
  const [loadedId, setLoadedId] = useState<string | null>(null);

  if (broadcast && loadedId !== broadcast.id) {
    setLoadedId(broadcast.id);
    setTitle(broadcast.title ?? '');
    setBodyText(broadcast.body_text ?? '');
    setButtons(broadcast.buttons ?? []);
  }

  const isDraft = broadcast?.status === 'draft';
  const active = broadcast ? isActive(broadcast.status) : false;

  // Есть ли несохранённые правки формы (для подсказки у «Сохранить»).
  const isDirty =
    !!broadcast &&
    (title !== (broadcast.title ?? '') ||
      bodyText !== (broadcast.body_text ?? '') ||
      JSON.stringify(buttons.filter((b) => b.text.trim() && b.url.trim())) !==
        JSON.stringify(broadcast.buttons ?? []));

  const cleanButtons = () =>
    buttons.filter((b) => b.text.trim() && b.url.trim());

  const handleSave = () => {
    if (!broadcast) return;
    updateMutation.mutate(
      {
        broadcastId: broadcast.id,
        payload: {
          title: title.trim() ? title.trim() : null,
          body_text: bodyText,
          buttons: cleanButtons(),
        },
      },
      {
        onSuccess: () => message.success('Черновик сохранён'),
        onError: (e) => message.error(extractErrorMessage(e)),
      },
    );
  };

  const handleSend = () => {
    if (!broadcast) return;
    const count = recipients?.recipients_count ?? 0;
    const btns = cleanButtons();
    const text = bodyText.trim();
    const mediaCount = broadcast.media.length;

    Modal.confirm({
      title: 'Отправить рассылку?',
      width: 520,
      content: (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Text>
            Получат <b>{count}</b>{' '}
            {BROADCAST_PLATFORM_LABELS[broadcast.platform] ?? broadcast.platform}
            -подписчиков. Уйдёт:
          </Text>

          <div
            style={{
              border: '1px solid #E5E7EB',
              borderRadius: 6,
              padding: '8px 12px',
              background: '#F9FAFB',
            }}
          >
            {text ? (
              <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {text.length > 200 ? `${text.slice(0, 200)}…` : text}
              </Text>
            ) : (
              <Text type="danger" style={{ fontSize: 13 }}>
                Без текста
              </Text>
            )}
            <div style={{ marginTop: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Вложения: {mediaCount ? `${mediaCount} медиа` : 'нет'} ·{' '}
                {btns.length ? `кнопок: ${btns.length}` : 'без кнопок'}
              </Text>
            </div>
          </div>

          <Text type="warning" style={{ fontSize: 13 }}>
            Отправленные сообщения нельзя отозвать. Остановить можно только
            рассылку тем, кому ещё не ушло.
          </Text>
        </Space>
      ),
      okText: 'Отправить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          // 1) СНАЧАЛА сохраняем черновик — иначе несохранённые текст/кнопки
          //    молча потеряются и рассылка уйдёт неполной (необратимо).
          await updateMutation.mutateAsync({
            broadcastId: broadcast.id,
            payload: {
              title: title.trim() ? title.trim() : null,
              body_text: bodyText,
              buttons: btns,
            },
          });
          // 2) и только потом запускаем отправку.
          await sendMutation.mutateAsync(broadcast.id);
          message.success('Рассылка запущена');
        } catch (e) {
          message.error(extractErrorMessage(e));
          throw e; // оставляем модалку открытой
        }
      },
    });
  };

  const handleCancel = () => {
    if (!broadcast) return;
    cancelMutation.mutate(broadcast.id, {
      onSuccess: () => message.success('Рассылка остановлена'),
      onError: (e) => message.error(extractErrorMessage(e)),
    });
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (!broadcast) return Upload.LIST_IGNORE;
    if (broadcast.media.length >= MEDIA_LIMITS.maxItems) {
      message.error(`Максимум ${MEDIA_LIMITS.maxItems} медиа в одной рассылке`);
      return Upload.LIST_IGNORE;
    }
    const mime = (file.type || '').toLowerCase();
    const isImage = (MEDIA_LIMITS.imageMimes as readonly string[]).includes(mime);
    const isVideo = (MEDIA_LIMITS.videoMimes as readonly string[]).includes(mime);
    const isDoc = (MEDIA_LIMITS.docMimes as readonly string[]).includes(mime);
    if (!isImage && !isVideo && !isDoc) {
      message.error('Поддерживаются картинки, видео и документы (PDF, Office, архивы и др.)');
      return Upload.LIST_IGNORE;
    }
    const limit = isVideo
      ? MEDIA_LIMITS.maxVideoBytes
      : isDoc
        ? MEDIA_LIMITS.maxDocBytes
        : MEDIA_LIMITS.maxImageBytes;
    if (file.size > limit) {
      message.error(
        `Файл слишком большой (${formatSize(file.size)}). Лимит: ${limit / 1024 / 1024} МБ`,
      );
      return Upload.LIST_IGNORE;
    }
    uploadMutation.mutate(
      { broadcastId: broadcast.id, file: file as unknown as File },
      {
        onSuccess: () => message.success('Файл добавлен'),
        onError: (e) => message.error(extractErrorMessage(e)),
      },
    );
    return Upload.LIST_IGNORE;
  };

  const total = broadcast?.total_count ?? 0;
  const done = broadcast ? broadcast.sent_count + broadcast.failed_count : 0;
  const percent = total ? Math.round((done / total) * 100) : 0;

  return (
    <Drawer
      open={Boolean(broadcastId)}
      onClose={onClose}
      width={560}
      title={broadcast?.title || 'Рассылка'}
      destroyOnClose
      extra={
        isDraft ? (
          <Space>
            {isDirty && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Есть несохранённые правки
              </Text>
            )}
            <Button loading={updateMutation.isPending} onClick={handleSave}>
              Сохранить
            </Button>
            <Button type="primary" onClick={handleSend}>
              Отправить
            </Button>
          </Space>
        ) : active ? (
          <Popconfirm
            title="Остановить рассылку?"
            description="Уже отправленные сообщения не отзываются."
            okText="Остановить"
            cancelText="Нет"
            onConfirm={handleCancel}
          >
            <Button danger loading={cancelMutation.isPending}>
              Остановить
            </Button>
          </Popconfirm>
        ) : null
      }
    >
      {isLoading || !broadcast ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <Space size={8} wrap>
            <Tag>
              {BROADCAST_PLATFORM_LABELS[broadcast.platform] ?? broadcast.platform}
            </Tag>
            <Tag color={BROADCAST_STATUS_META[broadcast.status]?.color}>
              {BROADCAST_STATUS_META[broadcast.status]?.label ?? broadcast.status}
            </Tag>
            {isDraft && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Получателей: {recipients ? recipients.recipients_count : '…'}
              </Text>
            )}
          </Space>

          {!isDraft && (
            <BroadcastStatsBlock
              stats={stats}
              loading={statsLoading}
              running={active}
              percent={percent}
            />
          )}

          {broadcast.status === 'cancelled' && (
            <Alert
              type="warning"
              showIcon
              message="Рассылка остановлена"
              description={`Успели отправить ${broadcast.sent_count} из ${total}. Остальным сообщение не ушло.`}
            />
          )}

          {!isDraft && !active && broadcast.status !== 'done' && broadcast.status !== 'cancelled' && (
            <Alert type="info" showIcon message={`Статус: ${broadcast.status}`} />
          )}

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Название (для себя)
            </Text>
            <Input
              value={title}
              disabled={!isDraft}
              maxLength={255}
              onChange={(e) => setTitle(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Текст сообщения
            </Text>
            <TextArea
              value={bodyText}
              disabled={!isDraft}
              rows={8}
              placeholder="Что получат подписчики"
              onChange={(e) => setBodyText(e.target.value)}
              style={{ marginTop: 4 }}
            />
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Медиа ({broadcast.media.length} из {MEDIA_LIMITS.maxItems})
            </Text>
            <div style={{ marginTop: 8 }}>
              {broadcast.media.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Файлов нет
                </Text>
              ) : (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {broadcast.media.map((m, i) => (
                    <div
                      key={m.s3_key}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #E5E7EB',
                        borderRadius: 6,
                        padding: '6px 12px',
                      }}
                    >
                      <Text style={{ fontSize: 13 }}>
                        {m.type === 'document'
                          ? (m.filename || 'Документ')
                          : m.type === 'video' ? 'Видео' : 'Картинка'} ·{' '}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatSize(m.size)}
                        </Text>
                      </Text>
                      {isDraft && (
                        <Button
                          size="small"
                          type="text"
                          danger
                          loading={deleteMediaMutation.isPending}
                          onClick={() =>
                            deleteMediaMutation.mutate(
                              { broadcastId: broadcast.id, index: i },
                              { onError: (e) => message.error(extractErrorMessage(e)) },
                            )
                          }
                        >
                          Убрать
                        </Button>
                      )}
                    </div>
                  ))}
                </Space>
              )}
            </div>

            {isDraft && broadcast.media.length < MEDIA_LIMITS.maxItems && (
              <Upload
                beforeUpload={beforeUpload}
                showUploadList={false}
                multiple={false}
                accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.zip,.rar,.7z,.json,.xml"
              >
                <Button style={{ marginTop: 8 }} loading={uploadMutation.isPending}>
                  Добавить файл
                </Button>
              </Upload>
            )}
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Картинки до 10 МБ, видео и документы до 50 МБ. Несколько картинок уйдут альбомом.
              </Text>
            </div>
          </div>

          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Кнопки со ссылкой
            </Text>
            <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
              {buttons.map((b, i) => (
                <Space.Compact key={i} style={{ width: '100%' }}>
                  <Input
                    value={b.text}
                    disabled={!isDraft}
                    maxLength={64}
                    placeholder="Текст кнопки"
                    onChange={(e) =>
                      setButtons((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)),
                      )
                    }
                  />
                  <Input
                    value={b.url}
                    disabled={!isDraft}
                    placeholder="https://..."
                    onChange={(e) =>
                      setButtons((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)),
                      )
                    }
                  />
                  {isDraft && (
                    <Button
                      onClick={() => setButtons((prev) => prev.filter((_, j) => j !== i))}
                    >
                      ✕
                    </Button>
                  )}
                </Space.Compact>
              ))}
              {isDraft && buttons.length < 3 && (
                <Button onClick={() => setButtons((prev) => [...prev, { text: '', url: '' }])}>
                  Добавить кнопку
                </Button>
              )}
            </Space>
            {isDraft && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  «Отправить» сохранит черновик автоматически — отдельно нажимать
                  «Сохранить» не обязательно.
                </Text>
              </div>
            )}
          </div>
        </Space>
      )}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Статистика доставки (N-5-a)                                         */
/* ------------------------------------------------------------------ */

/** Счётчик в стиле вкладки «Статистика» (дизайн «Графит»). */
function StatCounter({
  label,
  value,
  loading,
  tone,
}: {
  label: string;
  value?: number;
  loading: boolean;
  tone?: 'danger' | 'muted';
}) {
  const color =
    tone === 'danger' ? '#B91C1C' : tone === 'muted' ? '#6B7280' : '#1F2937';
  return (
    <Card styles={{ body: { padding: 16 } }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
      {loading ? (
        <Skeleton active paragraph={false} title={{ width: 48 }} />
      ) : (
        <Title level={3} style={{ margin: '4px 0 0', fontWeight: 500, color }}>
          {value ?? 0}
        </Title>
      )}
    </Card>
  );
}

function formatDuration(sec: number | null): string | null {
  if (sec === null) return null;
  if (sec < 60) return `${sec} сек`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m} мин ${s} сек` : `${m} мин`;
}

/**
 * Блок статистики доставки рассылки.
 *
 * ⚠️ «Прочитано» здесь нет намеренно: мессенджеры не отдают read-receipts для
 * исходящих сообщений бота. Показываем только то, что знаем достоверно —
 * доставлено / ошибки / заблокировали бота / осталось.
 */
function BroadcastStatsBlock({
  stats,
  loading,
  running,
  percent,
}: {
  stats?: BroadcastStats;
  loading: boolean;
  running: boolean;
  percent: number;
}) {
  const notDelivered = (stats?.failed ?? 0) + (stats?.blocked ?? 0);
  const duration = formatDuration(stats?.duration_sec ?? null);

  return (
    <Card size="small" styles={{ body: { padding: 16 } }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {running ? 'Идёт отправка' : 'Доставка'}
          </Text>
          {duration && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Заняла {duration}
            </Text>
          )}
        </div>

        {(running || (stats?.pending ?? 0) > 0) && (
          <Progress
            percent={running ? percent : 100}
            status={running ? 'active' : 'normal'}
          />
        )}

        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          }}
        >
          <StatCounter label="Всего" value={stats?.total} loading={loading} />
          <StatCounter label="Доставлено" value={stats?.sent} loading={loading} />
          <StatCounter
            label="Не доставлено"
            value={notDelivered}
            loading={loading}
            tone={notDelivered > 0 ? 'danger' : undefined}
          />
          {(stats?.pending ?? 0) > 0 && (
            <StatCounter
              label="Осталось"
              value={stats?.pending}
              loading={loading}
              tone="muted"
            />
          )}
        </div>

        {(stats?.blocked ?? 0) > 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Из них {stats?.blocked} — заблокировали бота или удалили аккаунт.
            Это не ошибка рассылки.
          </Text>
        )}

        {stats && stats.failed_recipients.length > 0 && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: '#6B7280' }}>
              Кому не дошло ({stats.failed_recipients.length})
            </summary>
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
              <Table
                size="small"
                pagination={false}
                rowKey={(r) => `${r.external_id}-${r.status}`}
                dataSource={stats.failed_recipients}
                columns={[
                  {
                    title: 'Получатель',
                    dataIndex: 'external_id',
                    width: 130,
                    render: (v: string) => (
                      <Text style={{ fontSize: 12 }}>{v}</Text>
                    ),
                  },
                  {
                    title: 'Причина',
                    dataIndex: 'error',
                    render: (e: string | null, row) => (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {row.status === 'blocked'
                          ? 'Заблокировал бота'
                          : e || 'Ошибка отправки'}
                      </Text>
                    ),
                  },
                ]}
              />
            </div>
          </details>
        )}

        <Text type="secondary" style={{ fontSize: 11 }}>
          «Прочитано» мессенджеры не сообщают — показываем только факт доставки.
        </Text>
      </Space>
    </Card>
  );
}
